import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	unstable_HistoryRouter as Router,
    Routes, 
    Route,
    Navigate
} from 'react-router';
import { createInstance, MatomoProvider } from '@streamr/matomo-tracker-react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import Page from './components/Page'
import GlobalLoader from './components/GlobalLoader'
import NotFoundRedirect from './components/NotFoundRedirect'
import JSONDataContext from './context'
import {
	loader_global_show,
	loader_global_hide,
	resize_window,
	orientation_change,
	intern_is_initial_config_request_done,
	intern_set_is_authenticated
} from './actions'
import { 
	BREAKPOINTS,
	JSON_DATA_URL,
	AUTH_URLS,
	MATOMO_TRACKING_DISABLED,
	MATOMO_BASE_URL,
	MATOMO_SITE_ID,
	MATOMO_DOMAIN_ID_DICT,
	MATOMO_USER_ID,
	MATOMO_HB_ACTIVE,
	MATOMO_HB_SECONDS,
	MATOMO_LINK_TRACKING
} from './utils/AppConfig'
import { resolve_match_url } from './utils/Router'
import { app_history } from './reducer'

import { get_cookie } from './utils/Cookies';


const App = () => {
	const {i18n} = useTranslation();
	
	// used to set the JSONDataContext Provider value set later
	const [json_data, set_json_data] = useState();
	const [tracker_instance, set_tracker_instance] = useState();
	const dispatch = useDispatch();
	
	const setup_axios_security = () => {
		/* 
			axios struggles with some cross site domain requests
			we use this interceptor to add our csrf information
			on every request.
		*/
	    axios.defaults.withCredentials = true;
	
	    axios.interceptors.request.use((config) => {
	
	        const token = get_cookie('csrftoken');
	
	        if (token) {
	            config.headers['X-CSRFToken'] = token;
	        }
	
	        return config;
	    }, (error) => {
	        return Promise.reject(error);
	    });
	};
	
	
	useEffect(() => {
		
		const initialize_app = async () => {
	        /* Axios configuration for Django built-in tools (Sessions & CSRF).
	            Enables automatic handling of the csrftoken cookie.
	        */
		   
			setup_axios_security()
	
	        dispatch(loader_global_show());
	
	        try {
	            /* load the json data from the specific url and have it
	                ready to publish it to the JSONDataContext.
	            */
	            const response_json = await axios.get(JSON_DATA_URL);
	            set_json_data(response_json.data);
	
	            /* Check if we are logged in.
	                This is performed sequentially after the JSON load to 
	                ensure the CSRF cookie is processed by the browser.
	            */
	            const response_auth = await axios.get(AUTH_URLS.STATUS);
	            dispatch(intern_is_initial_config_request_done());
	            dispatch(intern_set_is_authenticated(response_auth.data.is_authenticated));
	
	            /* Initialize tracking and domain-specific settings.
	            */
	            const domain_name = response_auth.data.domain;
	            console.log('response_auth.data: ', response_auth.data)
	            let current_site_id = MATOMO_SITE_ID;
	
	            if (process.env.NODE_ENV !== 'development') {
	                if (domain_name in MATOMO_DOMAIN_ID_DICT) {
	                    current_site_id = MATOMO_DOMAIN_ID_DICT[domain_name];
	                } else {
	                    current_site_id = MATOMO_DOMAIN_ID_DICT['default'];
	                }
	            }
	
	            const tracker_instance = createInstance({
	                urlBase: MATOMO_BASE_URL,
	                siteId: current_site_id,
	                userId: MATOMO_USER_ID,
	                disabled: MATOMO_TRACKING_DISABLED,
	                heartBeat: {
	                    active: MATOMO_HB_ACTIVE,
	                    seconds: MATOMO_HB_SECONDS
	                },
	                linkTracking: MATOMO_LINK_TRACKING,
	                configurations: {
	                    disableCookies: true,
	                    setSecureCookie: response_auth.data.is_secure, // should be true on https
	                    setRequestMethod: 'POST'
	                }
	            });
	            set_tracker_instance(tracker_instance);
	
	        } catch (error) {
	            /* Error handling for the initialization chain.
	                If any request fails, we hide the loader and log the error.
	            */
	            dispatch(loader_global_hide());
	            /* we do not set json to loaded, perhaps there should be an
	               error message to the end user: something like visit later */
	            console.error("Initialization failed:", error);
	        }
	    };
	    
	    // start loading
		initialize_app();
	    
		/*
			update window size if user resizes
			the window
		*/
		const handle_window_resize = () => {
			const w = window.innerWidth
			const h = window.innerHeight
			let client_type = 'desktop'
			if(w <= BREAKPOINTS.mobile) {
				client_type = 'mobile'
			} else if(w >= BREAKPOINTS.medium_min && w <= BREAKPOINTS.medium_max) {
				client_type = 'tablet'
			}
			// console.log('client_type: '+client_type)
			dispatch(resize_window(w, h, client_type));
		}
		
		/*
			update the orientation if the user
			rotates the device. IOS needs a little
			pause...
		*/
		const handle_orientation_change = () => {
			let orientation = 'unknown';
			if (window.screen.orientation) {
			  orientation = window.screen.orientation.type;
			} else if (window.orientation !== undefined) {
			  switch (window.orientation) {
			    case 0:
			      orientation = 'portrait-primary';
			      break;
			    case 180:
			      orientation = 'portrait-secondary';
			      break;
			    case 90:
			      orientation = 'landscape-primary';
			      break;
			    case -90:
			      orientation = 'landscape-secondary';
			      break;
				default:
				  orientation = 'portrait-primary';
			  }
			}
    		console.log(`change orientation to: ${orientation}`);
    		setTimeout(() => { // short wait
				dispatch(orientation_change(orientation));
			}, 300);
		}
		
		window.addEventListener("resize", handle_window_resize);
		window.addEventListener("orientationchange", handle_orientation_change);
		// initial manual call
		handle_window_resize();
		return () => {
			window.removeEventListener("resize", handle_window_resize);
			window.removeEventListener("orientationchange", handle_orientation_change);
		}
		
	}, [dispatch]);
	
	return (
		<div className="app-container">
			<MatomoProvider value={tracker_instance}>
			<JSONDataContext.Provider value={json_data}>
					<Router history={app_history}>
						<GlobalLoader />
						<Routes>
							<Route path={resolve_match_url('station_from', 'de')} element={<Page />} />
							<Route path={resolve_match_url('station_from_to', 'de')} element={<Page />} />
							<Route path={resolve_match_url('line', 'de')} element={<Page />} />
							<Route path={resolve_match_url('home', 'de')} element={<Page />} />
							
							<Route path={resolve_match_url('station_from', 'fr')} element={<Page />} />
							<Route path={resolve_match_url('station_from_to', 'fr')} element={<Page />} />
							<Route path={resolve_match_url('line', 'fr')} element={<Page />} />
							<Route path={resolve_match_url('home', 'fr')} element={<Page />} />
							
							<Route path={resolve_match_url('station_from', 'it')} element={<Page />} />
							<Route path={resolve_match_url('station_from_to', 'it')} element={<Page />} />
							<Route path={resolve_match_url('line', 'it')} element={<Page />} />
							<Route path={resolve_match_url('home', 'it')} element={<Page />} />
							
							<Route path={resolve_match_url('station_from', 'en')} element={<Page />} />
							<Route path={resolve_match_url('station_from_to', 'en')} element={<Page />} />
							<Route path={resolve_match_url('line', 'en')} element={<Page />} />
							<Route path={resolve_match_url('home', 'en')} element={<Page />} />
							
							<Route 
								path="/"
								element={<Navigate to={`/${i18n.language}/`} replace />}
							/>
							<Route path="/de/intern" element={<Page />} />
							<Route path="/fr/intern" element={<Page />} />
							<Route path="/it/intern" element={<Page />} />
							<Route path="/en/intern" element={<Page />} />
							<Route
								path="*" // everything that is not matched above
								element={<NotFoundRedirect />} 
							/>
						</Routes>
					</Router>
			</JSONDataContext.Provider>
			</MatomoProvider>
		</div>
	);
}

export default App;
